// NOTIFICATION_COPY — the frontend half of the notification contract.
//
// Backend registry: `app/modules/notification/service/registry.py`
// Backend spec:     `docs/spec/notification/README.md` §4 step 5
//
// This map is the authority on what renders. A kind that is missing here is
// **not rendered at all** — not shown with a raw kind string, not shown with
// empty copy. That is deliberate (ADR-0060, N17): the W35.7 audit found raw
// English enums (`notes`, `checklist`) sitting in a zh-Hant interface, and the
// fix is to make the failure mode "invisible" rather than "almost right".
//
// The trade-off is that the backend can ship a kind before the frontend knows
// about it, and users will see nothing until it lands here. That is the correct
// direction for the failure: a missing notification is invisible, whereas a
// wrong one is a bug the user has to report.
//
// Adding a kind:
//   1. Add an entry keyed by `InAppNotification.kind`.
//   2. Write the copy in 繁體中文 (AGENTS §3 #14). Never interpolate a raw id.
//   3. Return the deep link in `href`, or `null` if the kind has no destination.
//   4. Add the matching key to `NOTIFICATION_KINDS` so the test that keeps the
//      two registries in sync can see it.

import type { NotificationItem } from "@/lib/api/dashboard";

/** Facts the backend stored in `payload`. Every field is optional-by-design. */
export type NotificationPayload = Record<string, unknown>;

export interface NotificationCopy {
  /** 繁體中文 one-liner. Must be readable without opening anything. */
  title: (payload: NotificationPayload) => string;
  /** Deep link, or `null` for kinds with nowhere to go. */
  href: (payload: NotificationPayload) => string | null;
  /** Short label for the 「查看詳情」 affordance; omitted when `href` is null. */
  action?: string;
}

const str = (payload: NotificationPayload, key: string): string | null => {
  const value = payload[key];
  if (value === null || value === undefined) return null;
  const text = String(value);
  return text.length > 0 ? text : null;
};

const num = (payload: NotificationPayload, key: string): number | null => {
  const value = payload[key];
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

/** 「還有 7 天」/「就是今天」— the two shapes a reminder can take. */
function daysUntilCopy(days: number | null): string {
  if (days === null) return "紀念日提醒";
  if (days <= 0) return "就是今天";
  if (days === 1) return "還有 1 天";
  return `還有 ${days} 天`;
}

function milestoneCopy(payload: NotificationPayload): string {
  const kind = str(payload, "kind");
  const threshold = num(payload, "threshold_value");
  if (kind === "days_100") return "我們在一起 100 天了";
  if (kind === "days_365") return "我們在一起滿一年了";
  if (kind === "years_n" && threshold !== null) return `第 ${threshold} 週年`;
  return "紀念日里程碑達成了";
}

export const NOTIFICATION_COPY: Record<string, NotificationCopy> = {
  "diary.liked": {
    title: () => "TA 喜歡了你的日記",
    href: (p) => {
      const id = str(p, "diary_id");
      return id ? `/diaries/${id}` : null;
    },
  },
  "diary.comment.created": {
    title: () => "TA 留言在你的日記下面",
    href: (p) => {
      const id = str(p, "diary_id");
      return id ? `/diaries/${id}` : null;
    },
  },
  "wish.completed": {
    title: () => "你們完成了一個心願",
    href: (p) => {
      const id = str(p, "wish_id");
      return id ? `/wishes/${id}` : null;
    },
  },
  "anniversary.milestone_reached": {
    title: (p) => milestoneCopy(p),
    href: (p) => {
      const id = str(p, "anniversary_id");
      return id ? `/anniversaries/${id}` : null;
    },
  },
  anniversary_reminder: {
    title: (p) => {
      const title = str(p, "title");
      const when = daysUntilCopy(num(p, "days_until"));
      return title ? `「${title}」${when}` : `紀念日${when}`;
    },
    href: (p) => {
      const id = str(p, "anniversary_id");
      return id ? `/anniversaries/${id}` : null;
    },
  },
  daily_message_updated: {
    title: (p) => {
      const date = str(p, "date");
      return date ? `TA 更新了 ${date} 的留言` : "TA 更新了留言";
    },
    href: () => "/dashboard/messages",
  },
};

/**
 * Kinds this build renders. Kept beside the map so a test can assert the two
 * agree — a key added to the map but not here is a kind nobody checks.
 */
export const NOTIFICATION_KINDS = Object.keys(NOTIFICATION_COPY);

/**
 * Chrome copy for the bell and its sheet.
 *
 * Lives here rather than beside the component because `react-refresh` only lets
 * a component module export components — mixing in a constants object silently
 * breaks Fast Refresh for everyone editing the bell.
 */
export const NOTIFICATION_I18N = {
  bell: "通知",
  openBell: "開啟通知",
  title: "站內通知",
  empty: "沒有新的通知",
  unread: "未讀",
  markRead: "標為已讀",
  /** One tap clears the badge (ADR-0069). */
  markAll: "全部已讀",
  markAllPending: "處理中…",
  open: "查看詳情",
} as const;

/** Copy for `item`, or `null` when the kind is unknown (N17: render nothing). */
export function copyFor(item: Pick<NotificationItem, "kind" | "payload">): NotificationCopy | null {
  return copyForKind(item.kind);
}

/**
 * Copy for a raw kind, or `null` when unknown.
 *
 * The `hasOwnProperty` check is load-bearing rather than defensive:
 * `NOTIFICATION_COPY` is a `Record`, so under `noUncheckedIndexedAccess` every
 * lookup is `NotificationCopy | undefined`. Collapsing that to an explicit
 * `null` at this boundary is what lets callers write `if (copy === null) return
 * null;` instead of a non-null assertion — which is exactly the assertion that
 * would have shipped the raw-kind bug this rule exists to prevent.
 */
export function copyForKind(kind: string): NotificationCopy | null {
  return Object.prototype.hasOwnProperty.call(NOTIFICATION_COPY, kind)
    ? (NOTIFICATION_COPY[kind] ?? null)
    : null;
}

/** True when this build knows how to render `kind`. */
export function isRenderableKind(kind: string): boolean {
  return copyForKind(kind) !== null;
}
